import type { ReactNode } from "react"
import React, { useEffect, useState } from "react"
import { BiPencil } from "react-icons/bi"
import { CgClose } from "react-icons/cg"

import type {
  ButtonGroupProps,
  IconButtonProps,
  StackProps,
} from "@chakra-ui/react"
import {
  ButtonGroup,
  Flex,
  IconButton,
  Stack,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react"

import { CheckmarkIcon, Confirmation, valuesAreEqual } from "@supliful/common"

export type InlineEditableProps<Value> = {
  initialValue: Value
  onSave: (value: Value) => void
  confirmationText?: (value: Value) => ReactNode
  confirmationTitle?: string
  renderPreview?: (value: Value) => ReactNode
  renderInput: (value: Value, onChange: (value: Value) => void) => ReactNode
  wrapperProps?: Partial<StackProps>
  editButtonProps?: Partial<IconButtonProps>
  saveLabel?: string
}

type EditControlsProps = ButtonGroupProps & {
  onCancel: () => void
}

export function InlineEditable<T>({
  initialValue,
  onSave,
  confirmationText,
  confirmationTitle,
  saveLabel,
  renderInput,
  renderPreview,
  wrapperProps,
  editButtonProps,
}: InlineEditableProps<T>) {
  const [isEditing, setIsEditing] = useState(false)
  const [currentValue, setCurrentValue] = useState(initialValue)
  const hasEdits = !valuesAreEqual(currentValue, initialValue)
  const confirmationProps = useDisclosure()

  useEffect(() => {
    setCurrentValue(initialValue)
  }, [initialValue])

  const handleChange = (value: T) => {
    setCurrentValue(value)
  }

  const handleSave = () => {
    onSave(currentValue)
    confirmationProps.onClose()
    setIsEditing(false)
  }

  const handleCancel = () => {
    setCurrentValue(initialValue)
    confirmationProps.onClose()
    setIsEditing(false)
  }

  const preview = renderPreview ? renderPreview(currentValue) : null
  const input = renderInput(currentValue, handleChange)
  const confirmation = confirmationText ? confirmationText(currentValue) : null

  const EnterEditModeButton = () => (
    <Flex justifyContent="center">
      <Tooltip label="Edit">
        <IconButton
          aria-label="Edit"
          size="sm"
          colorScheme="tertiary"
          onClick={() => setIsEditing(true)}
          icon={<BiPencil />}
          {...editButtonProps}
        />
      </Tooltip>
    </Flex>
  )

  const EditControls = ({ onCancel, ...rest }: EditControlsProps) => (
    <ButtonGroup justifyContent="center" size="sm" {...rest}>
      <Tooltip label={saveLabel || "Save changes"}>
        {confirmation ? (
          <Confirmation
            {...confirmationProps}
            title={confirmationTitle}
            text={confirmation}
            onSubmit={handleSave}
            onCancel={handleCancel}
          >
            <IconButton
              aria-label="Save"
              size="sm"
              colorScheme="primary"
              onClick={confirmationProps.onOpen}
              icon={<CheckmarkIcon />}
            />
          </Confirmation>
        ) : (
          <IconButton
            aria-label="Save"
            size="sm"
            colorScheme="primary"
            onClick={handleSave}
            icon={<CheckmarkIcon />}
          />
        )}
      </Tooltip>

      <Tooltip label="Reset changes">
        <IconButton
          aria-label="Cancel"
          size="sm"
          colorScheme="red"
          variant="ghost"
          onClick={onCancel}
          icon={<CgClose />}
        />
      </Tooltip>
    </ButtonGroup>
  )

  return (
    <Stack
      direction="row"
      align="center"
      textAlign="left"
      fontSize="16"
      w="full"
      spacing={4}
      {...wrapperProps}
    >
      <Flex minH={8} align="center" flexGrow={2}>
        {preview && !isEditing ? preview : input}
      </Flex>

      {preview ? (
        <>
          {isEditing ? (
            <EditControls onCancel={handleCancel} />
          ) : (
            <EnterEditModeButton />
          )}
        </>
      ) : (
        <EditControls
          onCancel={() => setCurrentValue(initialValue)}
          visibility={hasEdits ? "visible" : "hidden"}
        />
      )}
    </Stack>
  )
}
